<template>
    <div>
        <!-- Loading state -->
        <div v-if="labelsAreloading">
            <Skeleton
                backgroundColor="bg-gray-100"
                width="w-72"
                height="h-244"
            />
        </div>

        <!-- Error state - only show if hideWarning is false -->
        <div 
            v-else-if="!labelAvailable && !hideWarning" class="max-w-4xl mx-auto mb-8 p-6 text-center bg-red-50 border border-red-200 rounded-lg">
            <h3 class="text-red-700 font-semibold mb-2">We are experiencing technical difficulties.</h3>
            <p class="text-red-600">Please visit your MyAccount online portal later to view a label describing key features of your Breezeline's service option.</p>
        </div>

        <!-- Rendered component once API call has finished loading -->
        <div v-else-if="showBroadbandLabels && !labelsAreloading && hasLabelData()" 
             class="text-navy-dark w-full" 
             :class="[offerTitle !== selectedLabel ? 'hidden desktop:block' : 'pt-5 tablet:pt-0']">
            <h4 class="text-base text-center mb-4">
                {{Number(getProductLabelData(offerTitle).labels[0].downloadSpeed) / 1000}} Mbps {{getProductLabelData(offerTitle).labels[0].name}}
            </h4>
            <div class="productLabel bg-white text-left border-navy-dark pt-2 px-4" :class="columns === 2 ? '' : 'border'">
                <h5 class="text-2lg border-b-2 border-navy-dark">Broadband Facts</h5>
                <div class="text-sm font-bold">Breezeline</div>
                <!-- Product Name and Speed -->
                <div class="text-sm font-bold">
                    {{getProductLabelData(offerTitle).labels[0].name}}
                    {{Number(getProductLabelData(offerTitle).labels[0].downloadSpeed) / 1000}} Mbps
                </div>
                <div class="text-sm border-navy-dark border-b font-normal pb-1 mb-1">Fixed Broadband Consumer Disclosure</div>
                <div class="text-xs" :class="columns === 2 ? 'grid grid-cols-2 gap-3' : ''">
                    <div>
                        <div class="flex items-center justify-between text-sm font-bold border-navy-dark border-b mb-2">
                            <!-- Monthly  -->
                            <span class="font-semibold py-1">Monthly Price</span>
                            <!-- Promotional price. Takes in cents, divids it by 100 to put into dollars and put through the currency formatter mixin function -->
                            <span>
                                {{toCurrency(this.monthlyCharge)}}
                            </span>
                        </div>
                        <div class="text-xs">
                            <p class="border-b font-normal border-navy-dark pb-2 mb-2">
                                <span v-html="contractTerms" />
                                <br>
                                <a class="underline" target="_blank" href="/business/policies-and-agreements">Click Here</a> for Terms of Contract.
                            </p>
                            <div class="mb-1 border-b border-navy-dark">
                                <span class="font-bold">Additional Charges & Terms</span> <span class="font-normal italic">may apply</span>
                                <ul>
                                    <li class="font-bold ml-3">Provider Monthly Fees</li>
                                    <li class="flex font-normal justify-between ml-3">
                                        <div>WYW for Business</div>
                                        <div>$19.99</div>
                                    </li>
                                    <li class="flex font-normal justify-between ml-3">
                                        <div>Internet Equipment with WiFi Your Way</div>
                                        <div>$12.99</div>
                                    </li>
                                    <li class="flex font-normal justify-between ml-3">
                                        <div>Additional WiFi Extender</div>
                                        <div>$4.99</div>
                                    </li>
                                    <li class="font-bold ml-3">One-time Fees at the Time of Purchase</li>
                                    <li class="flex font-normal justify-between ml-3">
                                        <div>Professional Installation</div>
                                        <div>$150.00</div>
                                    </li>
                                    <li class="flex font-normal justify-between ml-3">
                                        <div>Restart of Service</div>
                                        <div>$40.00</div>
                                    </li>
                                    <li class="flex font-normal justify-between ml-3">
                                        <div>Early Termination Fee</div>
                                    </li>
                                    <li class="font-bold ml-3">
                                        Up to the full amount of monthly recurring charges in the remaining service term.
                                    </li>
                                    <li class="flex font-normal justify-between ml-3">
                                        <div>HSD Recovery Fee</div>
                                        <div>$5.51</div>
                                    </li>
                                    <li class="flex font-normal justify-between ml-3">
                                        <div>Government Taxes</div>
                                        <div>Varies by Location</div>
                                    </li>
                                </ul>
                            </div>
                            <p class="font-normal border-b border-navy-dark mb-1">
                                <span class="block font-bold">Discounts & Bundles</span>
                                $10 Auto Pay discount. <a href="https://www.breezeline.com/business/policies-and-agreements" target="_blank" class="underline text-navy-dark">Click Here</a> for more information on all of our discounts and bundles.
                            </p>
                        </div>
                    </div>
                    <div>
                        <div class="border-b border-navy-dark pb-1 mb-1">
                            <span class="font-bold">Speeds Provided with Plan</span>
                            <ul>
                                <li class="flex justify-between ml-3" v-if="hasLabelData() && getProductLabelData(offerTitle).plan">
                                    <div class="font-normal">Typical Download Speed</div>
                                    <div class="font-bold">{{getProductLabelData(offerTitle).plan.typicalDownloadMbps}} Mbps</div>
                                </li>
                                <li class="flex justify-between ml-3" v-if="hasLabelData() && getProductLabelData(offerTitle).plan">
                                    <div class="font-normal">Typical Upload Speed</div>
                                    <div class="font-bold">{{getProductLabelData(offerTitle).plan.typicalUploadMbps}} Mbps</div>
                                </li>
                                <li class="flex justify-between ml-3" v-if="hasLabelData() && getProductLabelData(offerTitle).plan">
                                    <div class="font-normal">Typical Latency</div>
                                    <div class="font-bold">{{getProductLabelData(offerTitle).plan.typicalLatency}} ms</div>
                                </li>
                            </ul>
                        </div>
                        <div class="border-b border-navy-dark mb-1">
                            <div class="font-bold flex justify-between">
                                <div>Data Included with Monthly Price</div>
                                <div>Unlimited</div>
                            </div>
                            <div class="flex justify-between font-normal">
                                <div>Charges for Additional Data Usage</div>
                                <div>$0</div>
                            </div>
                        </div>
                        <div class="border-b border-navy-dark mb-1">
                            <div class="flex justify-between">
                                <div class="font-bold">Network Management</div>
                                <div class="font-normal">
                                    <a href="https://www.breezeline.com/business/policies-and-agreements" target="_blank">Read our Policy</a>
                                </div>
                            </div>
                            <div class="flex justify-between">
                                <div class="font-bold">Privacy</div>
                                <div class="font-normal">
                                    <a href="https://www.breezeline.com/business/policies-and-agreements" target="_blank">Read our Policy</a>
                                </div>
                            </div>
                        </div>
                        <div class="border-b border-navy-dark mb-1">
                            <div class="font-bold">Customer Support</div>
                            <div class="font-normal">Contact Us: <a class="text-navy-dark" href="/business/support">breezeline.com/business/support</a></div>
                            <div class="font-normal"><a class="text-navy-dark" href="tel:877-435-2227">877-435-2227</a></div>
                        </div>
                        <p class="font-normal">
                            Learn more about the terms used on this label by visiting the Federal Communications Commission Consumer Resource Center
                        </p>
                        <a class="block font-bold text-right ml-auto w-max mb-1 text-navy-dark" href="https://www.fcc.gov/consumers" target="_blank">
                            fcc.gov/consumer
                        </a>
                        <!-- Unique Plan Identifier Number -->
                        <div class="font-normal mb-1" v-if="hasLabelData()">
                            Unique Plan Identifier:<br> {{getProductLabelData(offerTitle).labels[0].labelFrnIdentifier}}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import Skeleton from '@/organisms/BroadbandLabel/Skeleton.vue'

export default {
    name: "BusinessLabel",
    components: {
        Skeleton
    },
    props: {
        showBroadbandLabels: {
            type: Boolean,
            default: true
        },
        selectedLabel: {
            type: String
        },
        columns: {
            type: Number,
            default: 1
        },
        offerTitle: {
            type: String,
            default: ''
        },
        bundlePremium: {
            type: Number,
            default: 99,
        },
        download: {
            type: Number,
            default: "1000",
        },
        upload: {
            type: Number,
            default: null,
        },
        monthlyCharge: {
            type: Number
        },
        contractTerms: {
            type: String,
            required: true,
        },
        contractLength: {
            type: String
        },
        hideWarning: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            labelHtml: this.getLabelHtml
        }
    },
    computed: {
        ...mapState('BroadbandLabels', [
            'labelData',
            'labelsAreloading',
            'labelAvailable'
        ]),
        ...mapState({
            offerId: state => state.meta.offerId,
            marketType: state => state.meta.marketType
        }),
        getLabelHtml() {
            const matchedData = this.getProductLabelData(this.offerTitle);
            if (matchedData && matchedData.labels && matchedData.labels.length > 0) {
                return matchedData.labels[0].labelHtml;
            }
            return '';
        }
    },
    methods: {
        ...mapActions('BroadbandLabels', [
            'fetchBroadbandLabel',
            'setLoadingState',
            'setLabelStatus'
        ]),
        toCent(amount) {
            const str = amount.toString()
            const int = str.split('.')
            return Number(amount.toFixed(2).replace('.', '').padEnd(int.length === 1 ? 3 : 4, '0'))
        },
        getProductLabelData(offerName) {
            if (!this.labelData || !Array.isArray(this.labelData)) {
                console.warn("labelData is not an array or undefined");
                return false;
            }
            
            const flattened = this.labelData.flat();
            const matchedData = flattened.find(data => {
                if (!data || !data.labels || !Array.isArray(data.labels)) {
                    console.warn("Invalid data object in labelData", data);
                    return false;
                }
                return data.labels.some(label => label.name === offerName);
            });
            
            return matchedData ? matchedData : false;
        },
        hasLabelData() {
            const data = this.getProductLabelData(this.offerTitle);
            return data && data.labels && data.labels.length > 0;
        },
        encodeOfferTitle(title) {
            const titleMap = {
                "Internet + Locals+": "Internet+%2B+Locals%2B",
                "Internet + Locals+ & Variety+": "Internet+%2B+Locals%2B+%26+Variety%2B",
                "Internet + Locals+, Variety+ & Family+": "Internet+%2B+Locals%2B,+Variety%2B+%26+Family%2B",
                "Internet + Standard Voice": "Internet+%2b+Standard+Voice",
                "Internet + Hosted Voice Essential": "Internet+%2b+Hosted+Voice+Essential",
                "Internet + Hosted Voice Essential Mobile": "Internet+%2b+Hosted+Voice+Essential+Mobile"
            };
            return titleMap[title] || title;
        }
    },
    watch: {
        labelData(newVal, oldVal) {
            if (newVal.length === 0 && oldVal.length > 0) {
                const encodedTitle = this.encodeOfferTitle(this.offerTitle);
                this.fetchBroadbandLabel({
                    "planName": encodedTitle,
                    "tier": this.download,
                    "downloadSpeed": this.download * 1000,
                    "uploadSpeed": this.upload * 1000,
                    "price": this.toCent(this.monthlyCharge),
                    "promotionalPrice": this.toCent(this.monthlyCharge).toString(),
                    "promotionalPriceDuration": this.contractLength || 12,
                    "offerId": "1",
                    "marketCode": "business",
                    "hasPromotionalPrice": true,
                }).catch(error => {
                    console.error('Error fetching business broadband label', error);
                    this.setLoadingState(false);
                    this.setLabelStatus(false);
                });
            }
        },
    },
    mounted() {
        if (this.getProductLabelData(this.offerTitle)) {
            this.setLoadingState(false);
        } else {
            const encodedTitle = this.encodeOfferTitle(this.offerTitle);
            
            this.fetchBroadbandLabel({
                "planName": encodedTitle,
                "tier": this.download,
                "downloadSpeed": this.download * 1000,
                "uploadSpeed": this.upload * 1000,
                "price": this.toCent(this.monthlyCharge),
                "promotionalPrice": this.toCent(this.monthlyCharge).toString(),
                "promotionalPriceDuration": this.contractLength || 12,
                "offerId": "1",
                "marketCode": "business",
                "hasPromotionalPrice": true,
            })
            .catch(error => {
                console.error('Error fetching business broadband label', error);
                this.setLoadingState(false);
                this.setLabelStatus(false);
            });
        }
    },
}
</script>

<style scoped>
/* Error state styles */
.text-red-600 {
    color: rgb(220, 38, 38);
}

.text-red-700 {
    color: rgb(185, 28, 28);
}

.bg-red-50 {
    background-color: rgb(254, 242, 242);
}

.border-red-200 {
    border-color: rgb(254, 202, 202);
}
</style>